import { useState, useEffect, useCallback, useRef } from 'react';
import { StudentService } from '../services';
import type { StudentListItem, PaginationInfo } from '../types';

const DEFAULT_PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 400;

export interface StudentFilters {
  searchName?: string;
  filterStatus?: string;
  filterLevel?: string;
  filterIsInactive?: string;
  sortOrder?: string;
  coachIds?: string[];
  projectCodes?: string[];
  sensitiveStatuses?: string[];
  redFlag?: 'all' | 'yes';
}

export function useStudents(
  initialPageSize: number = DEFAULT_PAGE_SIZE,
  filters: StudentFilters = {},
  controlledPage: number = 1,
) {
  const [students, setStudents] = useState<StudentListItem[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    pageSize: initialPageSize,
    totalRecords: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Keep a ref to the latest request so stale responses are ignored
  const reqId = useRef(0);

  const fetchPage = useCallback(
    async (
      page: number,
      pageSize: number = initialPageSize,
      serverFilters: StudentFilters = {}
    ) => {
      const currentReq = ++reqId.current;
      try {
        setLoading(true);
        setError(null);

        const response = await StudentService.listPaginated(page, pageSize, serverFilters);

        if (currentReq !== reqId.current) return;

        setStudents(response.data);
        setPagination(response.pagination);
      } catch (err: any) {
        if (currentReq !== reqId.current) return;
        setError(err.message || 'Erro ao carregar alunas');
      } finally {
        if (currentReq === reqId.current) setLoading(false);
      }
    },
    [initialPageSize]
  );

  // Debounce searchName changes, but react immediately to other filters
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const latestFilters = useRef(filters);
  const prevFilters = useRef(filters);
  latestFilters.current = filters;

  useEffect(() => {
    const currentFilters = latestFilters.current;
    const currentCoachIdsKey = (currentFilters.coachIds ?? []).join(',');
    const previousCoachIdsKey = (prevFilters.current.coachIds ?? []).join(',');
    const currentProjectCodesKey = (currentFilters.projectCodes ?? []).join(',');
    const previousProjectCodesKey = (prevFilters.current.projectCodes ?? []).join(',');
    const currentSensitiveStatusesKey = (currentFilters.sensitiveStatuses ?? []).join(',');
    const previousSensitiveStatusesKey = (prevFilters.current.sensitiveStatuses ?? []).join(',');

    const onlySearchChanged =
      currentFilters.filterStatus === prevFilters.current.filterStatus &&
      currentFilters.filterLevel === prevFilters.current.filterLevel &&
      currentFilters.filterIsInactive === prevFilters.current.filterIsInactive &&
      currentFilters.sortOrder === prevFilters.current.sortOrder &&
      currentCoachIdsKey === previousCoachIdsKey &&
      currentProjectCodesKey === previousProjectCodesKey &&
      currentSensitiveStatusesKey === previousSensitiveStatusesKey &&
      currentFilters.redFlag === prevFilters.current.redFlag &&
      currentFilters.searchName !== prevFilters.current.searchName;

    prevFilters.current = currentFilters;

    if (onlySearchChanged) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        fetchPage(controlledPage, initialPageSize, currentFilters);
      }, SEARCH_DEBOUNCE_MS);
    } else {
      clearTimeout(debounceTimer.current);
      fetchPage(controlledPage, initialPageSize, currentFilters);
    }

    return () => clearTimeout(debounceTimer.current);
  }, [controlledPage, filters.searchName, filters.filterStatus, filters.filterLevel, filters.filterIsInactive, filters.sortOrder, filters.coachIds, filters.projectCodes, filters.sensitiveStatuses, filters.redFlag, fetchPage, initialPageSize]);

  const goToPage = useCallback(
    (page: number) => fetchPage(page, pagination.pageSize, filters),
    [fetchPage, pagination.pageSize, filters]
  );

  const nextPage = useCallback(() => {
    if (pagination.hasNextPage) goToPage(pagination.page + 1);
  }, [pagination, goToPage]);

  const previousPage = useCallback(() => {
    if (pagination.hasPreviousPage) goToPage(pagination.page - 1);
  }, [pagination, goToPage]);

  const refresh = useCallback(() => {
    fetchPage(controlledPage, initialPageSize, filters);
  }, [controlledPage, fetchPage, filters, initialPageSize]);

  return {
    students,
    pagination,
    loading,
    error,
    goToPage,
    nextPage,
    previousPage,
    refresh,
  };
}
